import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Database, Plus, Search, Pencil, Trash2, History, X,
} from "lucide-react";
import { materialsApi } from "@/lib/api";
import { MATERIAL_TYPES, CALC_UNITS, fmtMoney, fmt } from "@/lib/calc";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = {
  name: "", material_type: "glulam_beam_column", wood_species: "",
  calc_unit: "m3", unit_price: "", currency: "EUR",
};

export default function MaterialLibrary() {
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [historyMat, setHistoryMat] = useState(null);

  const load = async (q) => {
    setLoading(true);
    try {
      setMaterials(await materialsApi.list(q));
    } catch (e) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ ...m, unit_price: String(m.unit_price) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Material name is required");
    const payload = {
      name: form.name.trim(),
      material_type: form.material_type,
      wood_species: form.wood_species,
      calc_unit: form.calc_unit,
      unit_price: parseFloat(form.unit_price) || 0,
      currency: "EUR",
    };
    try {
      if (editing) {
        await materialsApi.update(editing.id, payload);
        toast.success("Material updated \u2014 live projects will recalculate");
      } else {
        await materialsApi.create(payload);
        toast.success("Material added");
      }
      setOpen(false);
      load(search);
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const confirmDelete = async () => {
    try {
      await materialsApi.remove(deleteId);
      toast.success("Material deleted");
      setDeleteId(null);
      load(search);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 md:p-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-amber-600" /> Material Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Base unit prices used across all quotes. Price changes flow live into projects.
          </p>
        </div>
        <button onClick={openNew} data-testid="add-material-btn"
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Material
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="material-search-input" value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, species or type…" className="pl-9 bg-white" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-100">
              {["Material", "Type", "Species", "Unit", "Unit Price", "Updated", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 ${i >= 4 && i <= 4 ? "text-right" : ""}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : materials.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="materials-empty">
                No materials yet. Add your first one.
              </td></tr>
            ) : materials.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`material-row-${m.id}`}>
                <td className="px-4 py-3 text-sm font-semibold text-slate-900">{m.name}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{MATERIAL_TYPES[m.material_type] || m.material_type}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{m.wood_species || "\u2014"}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{CALC_UNITS[m.calc_unit]?.long || m.calc_unit}</td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-900 tabular-nums text-right">
                  {fmtMoney(m.unit_price)}
                  <span className="text-slate-400 font-normal"> / {CALC_UNITS[m.calc_unit]?.label}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">{(m.updated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setHistoryMat(m)} data-testid={`material-history-${m.id}`}
                      className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Price history">
                      <History className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(m)} data-testid={`material-edit-${m.id}`}
                      className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteId(m.id)} data-testid={`material-delete-${m.id}`}
                      className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Material" : "Add Material"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Material name</Label>
              <Input data-testid="material-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Glulam Beam GL24h" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Material type</Label>
                <Select value={form.material_type} onValueChange={(v) => setForm({ ...form, material_type: v })}>
                  <SelectTrigger data-testid="material-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MATERIAL_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Wood species</Label>
                <Input data-testid="material-species-input" value={form.wood_species}
                  onChange={(e) => setForm({ ...form, wood_species: e.target.value })} placeholder="Spruce" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Calculation unit</Label>
                <Select value={form.calc_unit} onValueChange={(v) => setForm({ ...form, calc_unit: v })}>
                  <SelectTrigger data-testid="material-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(CALC_UNITS).map((u) => (
                      <SelectItem key={u.key} value={u.key}>{u.long}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Unit price (€)</Label>
                <Input data-testid="material-price-input" type="number" step="0.01" value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="0.00" className="tabular-nums" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={save} data-testid="material-save-btn" className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyMat} onOpenChange={() => setHistoryMat(null)}>
        <DialogContent className="sm:max-w-md" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" /> Price history
            </DialogTitle>
          </DialogHeader>
          {historyMat && (
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-900 mb-3">{historyMat.name}</p>
              {(historyMat.price_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                  <span className="text-slate-500">{(h.date || "").slice(0, 10)}</span>
                  <span className="font-semibold tabular-nums text-slate-900">{fmtMoney(h.price)}</span>
                </div>
              ))}
              {(!historyMat.price_history || historyMat.price_history.length === 0) && (
                <p className="text-sm text-slate-400">No history yet.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete material?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes it from the library. Existing project lines keep their stored data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-material" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
